import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { Heading, Text } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import { BackButton, Button, SectionHeader } from '../components/common';
import { useClubPermissions } from '../hooks/useClubPermissions';
import { format } from 'date-fns';
import SessionRanking from '../components/session/SessionRanking';
import SessionTemplateForm from '../components/sessions/SessionTemplateForm';
import { sessionToEditFormData } from '../components/sessions/EditSessionModal';
import SessionReviewInteractionsTable from '../components/session/SessionReviewInteractionsTable';
import SessionReviewPlayerSummaryTable from '../components/session/SessionReviewPlayerSummaryTable';
import SessionGamesHistory from '../components/games/SessionGamesHistory';
import RecordPaymentModal from '../components/payments/RecordPaymentModal';
import {
  fetchPaymentSearchAliasesByClub,
  mergeAliasesIntoPlayers,
} from '../services/clubMemberPaymentAliasService';
import { 
  toggleSessionPayment, 
  bulkToggleSessionPayments,
  getBatchSessionPlayerPaidStatus
} from '../services/paymentLedgerService';
import {
  SessionWrapUpHeader,
  SessionWrapUpTabNav,
  SessionWrapUpPlayerFilter,
  SessionWrapUpStatusPanel,
  resolveWrapUpTab,
} from '../components/sessions/wrapup/SessionWrapUpChrome';
import SessionWrapUpPaymentsPanel from '../components/sessions/wrapup/SessionWrapUpPaymentsPanel';
import SessionAnalysisPanel from '../components/sessions/wrapup/SessionAnalysisPanel';
import { isSessionPaymentWrapUpComplete, sessionRequiresPaymentWrapUp } from '../components/sessions/hub/sessionHubUtils';
import { APP_PAGE_BLEED_ROOT, APP_PAGE_GUTTER } from '../utils/sessionMobileLayout';
import { useSessionScoringConfig } from '../hooks/useSessionScoringConfig';

const WRAP_UP_PAGE_SHELL = `${APP_PAGE_BLEED_ROOT} min-h-screen bg-background`;
const WRAP_UP_PAGE_INNER = `mx-auto max-w-app-shell ${APP_PAGE_GUTTER} pt-3 pb-8 md:pt-6`;

const SessionWrapUpDetail = ({ currentUser }) => {
  const { clubId, sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  // Tabs state (details | player-management | analysis | games | session-ranking | player-interactions | reopen)
  const [activeTab, setActiveTab] = useState('player-management');
  // Player Management data
  const [playersLoading, setPlayersLoading] = useState(false);
  const [playersError, setPlayersError] = useState(null);
  const [sessionPlayers, setSessionPlayers] = useState([]);
  const [mutating, setMutating] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  // Data for ranking tab reuse
  const [rankingPlayersById, setRankingPlayersById] = useState({});
  const [rankingSessionPlayerStatus, setRankingSessionPlayerStatus] = useState({});
  // Player filter for games/interactions tabs
  const [filterPlayers, setFilterPlayers] = useState([]);
  const [filterPlayersLoading, setFilterPlayersLoading] = useState(false);
  const [filterPlayersError, setFilterPlayersError] = useState(null);
  const [selectedFilterPlayer, setSelectedFilterPlayer] = useState(null);
  const filterPlayersLoadedRef = useRef(false);
  const [gamesPlayersById, setGamesPlayersById] = useState({});
  const [gamesRefreshKey, setGamesRefreshKey] = useState(0);
  const [rankingRefreshKey, setRankingRefreshKey] = useState(0);
  // Record Payment modal state
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false);
  const [selectedPlayerForPayment, setSelectedPlayerForPayment] = useState(null);
  /** session_players.player_id — expanded row for bank/transfer name hints (host/admin) */
  const [paymentAliasExpandedPlayerId, setPaymentAliasExpandedPlayerId] = useState(null);
  const paymentsSectionRef = useRef(null);
  const pendingPaymentsScrollRef = useRef(false);

  // Use club permissions hook to get club context from URL
  const { 
    club: selectedClub, 
    role: clubRole, 
    loading: clubLoading, 
    hasPermission, 
    error: clubError 
  } = useClubPermissions(clubId, currentUser);

  const isAdmin = currentUser?.role === 'admin' || 
                  currentUser?.role === 'super_admin' || 
                  clubRole === 'host' ||
                  clubRole === 'admin' ||
                  clubRole === 'owner';
  const scoringConfig = useSessionScoringConfig(session);
  const canCorrectScore = isAdmin && session?.session_status === 'completed';

  const paymentsWrapUpComplete = useMemo(
    () => isSessionPaymentWrapUpComplete(session, sessionPlayers),
    [session, sessionPlayers]
  );

  const requiresPaymentWrapUp = useMemo(
    () => sessionRequiresPaymentWrapUp(session),
    [session]
  );

  // Fetch session details
  useEffect(() => {
    if (sessionId && clubId) {
      fetchSessionDetails();
    }
  }, [sessionId, clubId]);

  // Reset filter options when session changes
  useEffect(() => {
    setFilterPlayers([]);
    setSelectedFilterPlayer(null);
    setFilterPlayersError(null);
    filterPlayersLoadedRef.current = false;
    setPaymentAliasExpandedPlayerId(null);
  }, [sessionId, clubId]);

  // Initialize tab from query param and sync on change
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    setActiveTab(resolveWrapUpTab(tabParam));
  }, [location.search]);

  const updateTabInUrl = useCallback((nextTab) => {
    const params = new URLSearchParams(location.search);
    params.set('tab', nextTab);
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true }
    );
    setActiveTab(nextTab);
  }, [location.pathname, location.search, navigate]);

  const handleGoToPayments = useCallback(() => {
    const alreadyOnPayments = activeTab === 'player-management';
    if (!alreadyOnPayments) {
      pendingPaymentsScrollRef.current = true;
    }
    updateTabInUrl('player-management');
    if (alreadyOnPayments) {
      paymentsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeTab, updateTabInUrl]);

  useEffect(() => {
    if (!pendingPaymentsScrollRef.current || activeTab !== 'player-management') return;
    pendingPaymentsScrollRef.current = false;
    paymentsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [activeTab]);

  const fetchSessionDetails = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          *,
          session_types (
            id,
            name,
            description
          )
        `)
        .eq('id', sessionId)
        .eq('club_id', clubId)
        .single();

      if (error) throw error;
      setSession(data);
    } catch (err) {
      console.error('Error fetching session details:', err);
      setError('Failed to load session details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch session players (read-only) for Player Management tab
  useEffect(() => {
    if (!sessionId || !clubId) return;
    // Load roster on payments (with payment status) and analysis (attendance / no-shows)
    if (activeTab !== 'player-management' && activeTab !== 'analysis') return;
    let isCancelled = false;
    const fetchPlayers = async () => {
      setPlayersLoading(true);
      setPlayersError(null);
      try {
        const { data, error } = await supabase
          .from('session_players')
          .select(`
            id,
            player_id,
            status,
            exit_reason,
            late_joiner_penalty,
            games_played,
            effective_games_played,
            players:player_id (
              id,
              first_name,
              last_name,
              gender
            )
          `)
          .eq('session_id', sessionId)
          .order('created_at', { ascending: true });

        if (error) throw error;

        // Fetch per-player games played for the session to determine no-shows accurately
        // players_session_games does not have session_id; fetch via session_games
        const { data: psgRows, error: psgError } = await supabase
          .from('session_games')
          .select('players_session_games(player_id)')
          .eq('session_id', sessionId);
        if (psgError) throw psgError;

        const gamesPlayedByPlayer = {};
        (psgRows || []).forEach(r => {
          (r.players_session_games || []).forEach(psg => {
            const pid = psg.player_id;
            gamesPlayedByPlayer[pid] = (gamesPlayedByPlayer[pid] || 0) + 1;
          });
        });

        // Prepare data for batch payment status lookup
        // Skip payment status check if session has no price
        const sessionPlayersWithPrice = (data || []).map(row => ({
          id: row.id,
          sessionPrice: session?.price_per_player || 0,
          playerId: row.player_id,
          player_id: row.player_id,
          ...row
        }));

        // Only fetch payment status if session has a price and we are on payments
        let paymentStatusMap = {};
        if (activeTab === 'player-management' && session?.price_per_player) {
          const paymentStatusResult = await getBatchSessionPlayerPaidStatus(sessionPlayersWithPrice);
          paymentStatusMap = paymentStatusResult.statusMap || {};
        }

        // Compute payment status for each session_player
        const merged = sessionPlayersWithPrice.map((row) => {
          const calculatedGames = gamesPlayedByPlayer[row.player_id] || 0;
          const gamesPlayed = row.games_played ?? calculatedGames;
          const effectiveGamesPlayed = row.effective_games_played ?? gamesPlayed;
          
          // Get payment status from batch result
          const paymentStatus = paymentStatusMap[row.id] || {
            isPaid: false,
            amountPaid: 0,
            paidViaLedger: false
          };
          
          return {
            ...row,
            games_played: gamesPlayed,
            effective_games_played: effectiveGamesPlayed,
            session_payment: paymentStatus.isPaid ? 'paid' : 'not_paid',
            paidViaLedger: paymentStatus.paidViaLedger,
            ledgerPaymentDate: paymentStatus.paymentDate,
            ledgerPaymentMethod: paymentStatus.paymentMethod,
            paymentSource: paymentStatus.paymentSource,
            payerId: paymentStatus.payerId,
            payerFirstName: paymentStatus.payerFirstName,
            payerLastName: paymentStatus.payerLastName,
            paymentId: paymentStatus.paymentId
          };
        });

        const aliasRes = await fetchPaymentSearchAliasesByClub(clubId);
        const aliasMap = aliasRes.success ? (aliasRes.map || {}) : {};
        const mergedWithAliases = merged.map((row) => ({
          ...row,
          payment_search_aliases: aliasMap[row.player_id] || [],
        }));

        if (!isCancelled) setSessionPlayers(mergedWithAliases);
      } catch (err) {
        console.error('Error loading session players:', err);
        if (!isCancelled) setPlayersError('Failed to load session players');
      } finally {
        if (!isCancelled) setPlayersLoading(false);
      }
    };

    fetchPlayers();
    return () => { isCancelled = true; };
  }, [activeTab, clubId, sessionId, refreshCounter, session?.price_per_player]);

  // Fetch players for filtering (all tabs)
  useEffect(() => {
    if (!sessionId || !clubId) return;
    if (filterPlayersLoadedRef.current) return;
    let isCancelled = false;

    const fetchFilterPlayers = async () => {
      setFilterPlayersLoading(true);
      setFilterPlayersError(null);
      try {
        const { data, error } = await supabase
          .from('session_players')
          .select(`
            player_id,
            players:player_id(
              id,
              first_name,
              last_name,
              gender
            )
          `)
          .eq('session_id', sessionId);

        if (error) throw error;
        if (isCancelled) return;

        const basePlayers = (data || []).map(row => {
          const firstName = row.players?.first_name || '';
          const lastName = row.players?.last_name || '';
          return {
            id: row.players?.id || row.player_id,
            first_name: firstName,
            last_name: lastName,
            gender: row.players?.gender || null,
            name: `${firstName} ${lastName}`.trim() || firstName || 'Unknown Player',
          };
        });

        const { data: gamesData, error: gamesError } = await supabase
          .from('session_games')
          .select(`
            players_session_games(
              player_id,
              player:player_id(
                id,
                first_name,
                last_name,
                gender
              )
            )
          `)
          .eq('session_id', sessionId);

        if (gamesError) throw gamesError;
        if (isCancelled) return;

        const playersById = new Map();
        basePlayers.forEach(player => {
          if (player.id) {
            playersById.set(player.id, player);
          }
        });

        (gamesData || []).forEach(game => {
          (game.players_session_games || []).forEach(psg => {
            const player = psg.player;
            const id = player?.id || psg.player_id;
            if (!id || playersById.has(id)) return;
            const firstName = player?.first_name || '';
            const lastName = player?.last_name || '';
            playersById.set(id, {
              id,
              first_name: firstName,
              last_name: lastName,
              gender: player?.gender || null,
              name: `${firstName} ${lastName}`.trim() || firstName || 'Unknown Player',
            });
          });
        });

        const mapped = Array.from(playersById.values()).sort((a, b) => {
          const nameA = `${a.first_name} ${a.last_name}`.trim().toLowerCase();
          const nameB = `${b.first_name} ${b.last_name}`.trim().toLowerCase();
          return nameA.localeCompare(nameB);
        });

        const aliasRes = await fetchPaymentSearchAliasesByClub(clubId);
        const withAliases = mergeAliasesIntoPlayers(
          mapped,
          aliasRes.success ? (aliasRes.map || {}) : {}
        );

        setFilterPlayers(withAliases);
        filterPlayersLoadedRef.current = true;
      } catch (err) {
        if (isCancelled) return;
        console.error('Error fetching players for filtering:', err);
        setFilterPlayersError('Failed to load players for filtering');
      } finally {
        if (!isCancelled) setFilterPlayersLoading(false);
      }
    };

    fetchFilterPlayers();
    return () => { isCancelled = true; };
  }, [activeTab, sessionId, clubId]);

  // Fetch minimal player data for ranking tab reuse of SessionRanking
  useEffect(() => {
    if (activeTab !== 'session-ranking') return;
    if (!sessionId || !clubId) return;
    let cancelled = false;
    const loadForRanking = async () => {
      try {
        const { data, error } = await supabase
          .from('session_players')
          .select(`
            player_id,
            status,
            players:player_id (
              id,
              first_name,
              last_name,
              gender
            )
          `)
          .eq('session_id', sessionId);
        if (error) throw error;
        if (cancelled) return;
        const byId = {};
        const statusMap = {};
        const playerIds = [];
        (data || []).forEach(row => {
          const p = row.players || {};
          byId[p.id] = {
            id: p.id,
            name: `${p.first_name || ''} ${p.last_name || ''}`.trim(),
            gender: p.gender || 'unknown'
          };
          statusMap[p.id] = row.status;
          if (p.id) playerIds.push(p.id);
        });

        // Fetch current ELO ratings for these players under this club/season
        if (playerIds.length > 0 && session?.season_id) {
          const { data: eloRatings, error: eloErr } = await supabase
            .from('player_club_elo_ratings')
            .select('player_id, mixed_doubles_elo_rating, level_doubles_elo_rating')
            .eq('club_id', clubId)
            .eq('season_id', session.season_id)
            .in('player_id', playerIds);
          if (!eloErr && eloRatings) {
            eloRatings.forEach(r => {
              if (byId[r.player_id]) {
                byId[r.player_id].mixed_elo_rating = r.mixed_doubles_elo_rating || 1200;
                byId[r.player_id].levels_elo_rating = r.level_doubles_elo_rating || 1200;
              }
            });
          }
        }
        setRankingPlayersById(byId);
        setRankingSessionPlayerStatus(statusMap);
      } catch (err) {
        console.error('WrapUp: failed loading players for ranking', err);
      }
    };
    loadForRanking();
    return () => { cancelled = true; };
  }, [activeTab, clubId, sessionId]);

  useEffect(() => {
    if (activeTab !== 'games') return;
    if (!sessionId || !clubId) return;
    let cancelled = false;
    const loadForGames = async () => {
      try {
        const { data, error } = await supabase
          .from('session_players')
          .select(`
            player_id,
            players:player_id (
              id,
              first_name,
              last_name,
              gender
            )
          `)
          .eq('session_id', sessionId);
        if (error) throw error;
        if (cancelled) return;
        const byId = {};
        const playerIds = [];
        (data || []).forEach((row) => {
          const p = row.players || {};
          if (!p.id) return;
          byId[p.id] = {
            id: p.id,
            first_name: p.first_name,
            last_name: p.last_name,
            gender: p.gender,
          };
          playerIds.push(p.id);
        });
        if (playerIds.length > 0 && session?.season_id) {
          const { data: eloRatings, error: eloErr } = await supabase
            .from('player_club_elo_ratings')
            .select(
              'player_id, mixed_elo_rating, levels_elo_rating, singles_elo_rating, mixed_games_played, levels_games_played, singles_games_played'
            )
            .eq('club_id', clubId)
            .eq('season_id', session.season_id)
            .in('player_id', playerIds);
          if (!eloErr && eloRatings) {
            eloRatings.forEach((r) => {
              if (!byId[r.player_id]) return;
              byId[r.player_id].mixed_elo_rating = r.mixed_elo_rating ?? 1200;
              byId[r.player_id].levels_elo_rating = r.levels_elo_rating ?? 1200;
              byId[r.player_id].singles_elo_rating = r.singles_elo_rating ?? 1200;
              byId[r.player_id].mixed_games_played = r.mixed_games_played ?? 0;
              byId[r.player_id].levels_games_played = r.levels_games_played ?? 0;
              byId[r.player_id].singles_games_played = r.singles_games_played ?? 0;
            });
          }
        }
        if (!cancelled) setGamesPlayersById(byId);
      } catch (err) {
        console.error('WrapUp: failed loading players for score correction', err);
      }
    };
    loadForGames();
    return () => {
      cancelled = true;
    };
  }, [activeTab, clubId, sessionId, session?.season_id, gamesRefreshKey]);

  const togglePayment = async (sp) => {
    setMutating(true);
    setPlayersError(null);
    try {
      const result = await toggleSessionPayment(
        sp.id,
        session,
        currentUser.id
      );

      if (!result.success) {
        throw new Error(result.error);
      }

      // Refresh session players to get updated payment status
      setRefreshCounter(prev => prev + 1);

    } catch (err) {
      console.error('Failed to toggle payment:', err);
      if (err.message && err.message.includes('ledger')) {
        setPlayersError('This payment was recorded via the ledger system. Please manage it from the Payment Tracking page.');
      } else {
        setPlayersError('Failed to update payment status: ' + err.message);
      }
    } finally {
      setMutating(false);
    }
  };

  const markAllAsPaid = async () => {
    if (!sessionId || !session) return;
    setMutating(true);
    setPlayersError(null);
    
    try {
      const sessionPlayerIds = sessionPlayers.map(p => p.id);
      
      const result = await bulkToggleSessionPayments(
        sessionPlayerIds,
        session,
        currentUser.id,
        'paid'
      );

      if (!result.success) {
        throw new Error(result.error);
      }

      // Show summary of results
      if (result.failed > 0) {
        const errorMsg = `Marked ${result.succeeded} as paid. ${result.failed} failed (may already be paid via ledger).`;
        setPlayersError(errorMsg);
      }

      // Refresh session players
      setRefreshCounter(prev => prev + 1);

    } catch (err) {
      console.error('Failed to mark all as paid:', err);
      setPlayersError('Failed to mark all as paid: ' + err.message);
    } finally {
      setMutating(false);
    }
  };

  const markAllAsUnpaid = async () => {
    if (!sessionId || !session) return;
    setMutating(true);
    setPlayersError(null);
    
    try {
      const sessionPlayerIds = sessionPlayers.map(p => p.id);
      
      const result = await bulkToggleSessionPayments(
        sessionPlayerIds,
        session,
        currentUser.id,
        'unpaid'
      );

      if (!result.success) {
        throw new Error(result.error);
      }

      // Show summary of results
      if (result.failed > 0) {
        const errorMsg = `Marked ${result.succeeded} as unpaid. ${result.failed} failed (payments managed by ledger cannot be undone here).`;
        setPlayersError(errorMsg);
      }

      // Refresh session players
      setRefreshCounter(prev => prev + 1);

    } catch (err) {
      console.error('Failed to mark all as unpaid:', err);
      setPlayersError('Failed to mark all as unpaid: ' + err.message);
    } finally {
      setMutating(false);
    }
  };

  const handleRecordPayment = (player) => {
    setSelectedPlayerForPayment(player);
    setShowRecordPaymentModal(true);
  };

  const handlePaymentRecorded = async (result) => {
    // Refresh session players by incrementing refresh counter
    setRefreshCounter(prev => prev + 1);
    setShowRecordPaymentModal(false);
    setSelectedPlayerForPayment(null);
  };

  const toggleNoShow = async (sp) => {
    const next = sp.exit_reason === 'no_show' ? null : 'no_show';
    setSessionPlayers(prev => prev.map(p => p.id === sp.id ? { ...p, exit_reason: next } : p));
    setMutating(true);
    try {
      const { error } = await supabase
        .from('session_players')
        .update({ exit_reason: next })
        .eq('id', sp.id);
      if (error) throw error;
    } catch (err) {
      console.error('Failed to toggle no-show:', err);
      setSessionPlayers(prev => prev.map(p => p.id === sp.id ? { ...p, exit_reason: sp.exit_reason } : p));
      setPlayersError('Failed to update no-show');
    } finally {
      setMutating(false);
    }
  };

  const updateSessionStatus = async (newStatus) => {
    try {
      setUpdating(true);
      const { error } = await supabase
        .from('sessions')
        .update({ session_status: newStatus })
        .eq('id', sessionId);

      if (error) throw error;
      
      setSession(prev => ({ ...prev, session_status: newStatus }));
    } catch (err) {
      console.error('Error updating session status:', err);
      setError('Failed to update session status');
    } finally {
      setUpdating(false);
    }
  };

  const handleSessionUpdated = async (sessionId, updatedData) => {
    try {
      setUpdating(true);
      
      // Update the session in the database
      const { error } = await supabase
        .from('sessions')
        .update(updatedData)
        .eq('id', sessionId);

      if (error) throw error;

      // Refresh session data after successful update
      await fetchSessionDetails();
    } catch (error) {
      console.error('Error updating session:', error);
      alert('Failed to update session: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'ongoing': return 'success';
      case 'completed': return 'warning';
      case 'archived': return 'secondary';
      case 'cancelled': return 'danger';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), 'EEEE, MMMM do, yyyy');
  };

  const formatTime = (timeString) => {
    return format(new Date(`2000-01-01T${timeString}`), 'h:mm a');
  };

  // Show loading state while fetching club permissions or session details
  if (clubLoading || loading) {
    return (
      <div className={WRAP_UP_PAGE_SHELL}>
        <div className={WRAP_UP_PAGE_INNER}>
          <Card variant="default" size="large" className="text-center py-12">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              <Text className="ml-3">Loading session details…</Text>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (clubError || !hasPermission || error || !session) {
    return (
      <div className={WRAP_UP_PAGE_SHELL}>
        <div className={WRAP_UP_PAGE_INNER}>
        <Card variant="default" size="large" className="text-center py-12">
          <Heading level={2} className="mb-4">Session not found</Heading>
          <Text className="mb-8 text-text-secondary">
            {clubError || error || 'This session could not be found or you do not have permission to access it.'}
          </Text>
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:space-x-4 sm:gap-0">
            <Button onClick={() => navigate(`/club/${clubId}/sessions/review`)} variant="primary">
              Back to Review sessions
            </Button>
            <Button onClick={() => navigate('/')} variant="outline">
              Go to Homepage
            </Button>
          </div>
        </Card>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className={WRAP_UP_PAGE_SHELL}>
        <div className={WRAP_UP_PAGE_INNER}>
        <Card variant="default" size="large" className="text-center py-12">
          <Heading level={2} className="mb-4">Admin access required</Heading>
          <Text className="mb-8">You need admin privileges to manage session wrap-up for {selectedClub.name}.</Text>
          <Button onClick={() => navigate(`/club/${clubId}/sessions/review`)} variant="primary">
            Back to Review sessions
          </Button>
        </Card>
        </div>
      </div>
    );
  }

  // Check if we came from club dashboard
  const fromClubDashboard = location.state?.fromClubDashboard;
  const dashboardClub = location.state?.selectedClub;
  const dashboardUserRole = location.state?.userRole;

  // Handle back navigation - go to club dashboard if we came from there
  const handleBack = () => {
    if (fromClubDashboard && dashboardClub && dashboardUserRole) {
      navigate('/club-analysis', {
        state: {
          selectedClub: dashboardClub,
          userRole: dashboardUserRole
        }
      });
    } else {
      navigate(`/club/${clubId}/sessions/review`);
    }
  };

  return (
    <div className={WRAP_UP_PAGE_SHELL}>
      <div className={WRAP_UP_PAGE_INNER}>
        <div className="mb-4">
          <BackButton onClick={handleBack}>
            {fromClubDashboard ? 'Back to Club Dashboard' : 'Back to Review sessions'}
          </BackButton>
        </div>

        <SessionWrapUpHeader
          session={session}
          selectedClub={selectedClub}
          formatDate={formatDate}
          formatTime={formatTime}
          getStatusVariant={getStatusVariant}
          isAdmin={isAdmin}
          updating={updating}
          paymentsComplete={paymentsWrapUpComplete}
          requiresPaymentWrapUp={requiresPaymentWrapUp}
          onComplete={() => updateSessionStatus('completed')}
          onArchive={() => updateSessionStatus('archived')}
          onGoToPayments={handleGoToPayments}
        />

        <SessionWrapUpTabNav activeTab={activeTab} onTabChange={updateTabInUrl} />

      {/* Tab Content */}
      {activeTab === 'player-management' ? (
        <div ref={paymentsSectionRef} className="scroll-mt-24">
        <Card variant="default" size="large" className="!p-4 sm:!p-6">
          <SessionWrapUpPlayerFilter
            filterPlayers={filterPlayers}
            selectedFilterPlayer={selectedFilterPlayer}
            onSelectPlayer={setSelectedFilterPlayer}
            onRemovePlayer={() => setSelectedFilterPlayer(null)}
            filterPlayersLoading={filterPlayersLoading}
            filterPlayersError={filterPlayersError}
            isAdmin={isAdmin}
          />
          <SessionWrapUpPaymentsPanel
            session={session}
            sessionPlayers={sessionPlayers}
            selectedFilterPlayer={selectedFilterPlayer}
            playersLoading={playersLoading}
            playersError={playersError}
            isAdmin={isAdmin}
            mutating={mutating}
            clubId={clubId}
            paymentAliasExpandedPlayerId={paymentAliasExpandedPlayerId}
            setPaymentAliasExpandedPlayerId={setPaymentAliasExpandedPlayerId}
            onTogglePayment={togglePayment}
            onToggleNoShow={toggleNoShow}
            onRecordPayment={handleRecordPayment}
            onMarkAllPaid={markAllAsPaid}
            onMarkAllUnpaid={markAllAsUnpaid}
            onAddPrice={() => updateTabInUrl('details')}
            onAliasesSaved={(sp, playerUuid, aliases) => {
              setSessionPlayers((prev) =>
                prev.map((row) =>
                  row.player_id === sp.player_id
                    ? { ...row, payment_search_aliases: aliases }
                    : row
                )
              );
              setFilterPlayers((prev) =>
                prev.map((p) => (p.id === playerUuid ? { ...p, payment_search_aliases: aliases } : p))
              );
            }}
          />
        </Card>
        </div>
      ) : activeTab === 'analysis' ? (
        <SessionAnalysisPanel
          sessionId={sessionId}
          sessionPlayers={sessionPlayers}
          playersLoading={playersLoading}
          onNavigateTab={updateTabInUrl}
          pointsTarget={scoringConfig.pointsTarget}
        />
      ) : activeTab === 'session-ranking' ? (
        <Card variant="default" size="large">
          <div className="p-4 sm:p-6">
            {session?.session_types?.name && session.session_types.name.toLowerCase().includes('competitive') ? (
              <SessionRanking 
                sessionId={sessionId}
                playersById={rankingPlayersById}
                selectedClub={selectedClub}
                season={{ id: session.season_id }}
                sessionPlayerStatus={rankingSessionPlayerStatus}
                staticMode={true}
                refreshKey={rankingRefreshKey}
              />
            ) : (
              <div className="text-center py-6 text-text-muted">
                Rankings are only available for competitive sessions.
              </div>
            )}
          </div>
        </Card>
      ) : activeTab === 'games' ? (
        <Card variant="default" size="large" className="!p-4 sm:!p-6">
          <div>
            <Heading level={3} className="mb-2">Games</Heading>
            <SessionGamesHistory
              sessionId={sessionId}
              hideHeader
              showTypeSummary
              canCorrectScore={canCorrectScore}
              sessionStatus={session?.session_status}
              scoringConfig={scoringConfig}
              playersById={gamesPlayersById}
              refreshKey={gamesRefreshKey}
              onScoreCorrected={async () => {
                setGamesRefreshKey((key) => key + 1);
                setRankingRefreshKey((key) => key + 1);
              }}
            />
          </div>
        </Card>
      ) : activeTab === 'player-interactions' ? (
        <div className="space-y-4 sm:space-y-5">
          <Card variant="default" size="small" className="!p-4 sm:!p-5">
            <SessionWrapUpPlayerFilter
              filterPlayers={filterPlayers}
              selectedFilterPlayer={selectedFilterPlayer}
              onSelectPlayer={setSelectedFilterPlayer}
              onRemovePlayer={() => setSelectedFilterPlayer(null)}
              filterPlayersLoading={filterPlayersLoading}
              filterPlayersError={filterPlayersError}
              isAdmin={isAdmin}
              className="!mb-0 !border-b-0 !pb-0"
            />
          </Card>
          <Card variant="default" size="small" className="!p-4 sm:!p-5">
            <SectionHeader
              title="By player"
              description="How many different teammates and opponents each person saw."
              infoText="Teammates and opponents count unique people, not games. Repeat people is how many others they shared a court with — partner or opponent — in 50% or more of their games."
              size="sm"
              variant="primary"
              className="!mb-4"
            />
            <SessionReviewPlayerSummaryTable
              sessionId={sessionId}
              filterPlayerIds={selectedFilterPlayer ? [selectedFilterPlayer.id] : undefined}
              layout="compact"
            />
          </Card>
          <Card variant="default" size="small" className="!p-4 sm:!p-5">
            <SectionHeader
              title="Every pairing"
              description="How often each pair shared a court, as partners or opponents."
              infoText="Same team and opponents are counted separately. The list is sorted by most games together."
              size="sm"
              variant="primary"
              className="!mb-4"
            />
            <SessionReviewInteractionsTable
              sessionId={sessionId}
              filterPlayerIds={selectedFilterPlayer ? [selectedFilterPlayer.id] : undefined}
              layout="compact"
            />
          </Card>
        </div>
      ) : activeTab === 'details' ? (
        <Card variant="default" size="large" className="!p-4 sm:!p-6">
          <Heading level={3} className="mb-1">
            Session details
          </Heading>
          <Text className="mb-5 text-sm text-text-secondary">
            Update price, time, location, and the other session settings.
          </Text>
          <SessionTemplateForm
            onSubmit={(preparedData) => handleSessionUpdated(session.id, preparedData)}
            initialData={sessionToEditFormData(session)}
            loading={updating}
            clubId={selectedClub?.id}
            isModal
            mode="session"
            creationMode="customize"
            collapsibleSections
          />
        </Card>
      ) : activeTab === 'reopen' ? (
        <SessionWrapUpStatusPanel
          session={session}
          updating={updating}
          onComplete={() => updateSessionStatus('completed')}
          onReopen={() =>
            updateSessionStatus(session.session_status === 'archived' ? 'completed' : 'ongoing')
          }
        />
      ) : null}

      {/* Record Payment Modal */}
      {showRecordPaymentModal && (
        <RecordPaymentModal
          isOpen={showRecordPaymentModal}
          onClose={() => {
            setShowRecordPaymentModal(false);
            setSelectedPlayerForPayment(null);
          }}
          clubId={clubId}
          onPaymentRecorded={handlePaymentRecorded}
          preselectedPlayer={selectedPlayerForPayment}
          defaultCurrency={session?.currency_code}
          prioritizeSessionId={session?.id}
        />
      )}
      </div>
    </div>
  );
};

export default SessionWrapUpDetail;
